/*
 * CONTRACT: McpKeyDialog DialogState-Maschine (RITA-1369)
 *
 * 1. Initialzustand: phase aus prop (CREATE oder REVOKE_CONFIRM), kein Plaintext
 * 2. CREATE -> SHOW_KEY: AUSSCHLIESSLICH nach erfolgreichem createMcpKey (key im Response)
 * 3. REVOKE_CONFIRM: AUSSCHLIESSLICH wenn Parent mode='revoke' mit id+label übergibt
 * 4. -> null/close: Close in SHOW_KEY (= Plaintext-Clearance), Cancel in REVOKE_CONFIRM, Revoke Erfolg
 * 5. Bei Fehler: phase bleibt, kein State-Leak
 * 6. Bei Remount/Neuladen: kein persistierter State
 * 7. Plaintext NIEMALS im Parent-State, localStorage oder Apollo-Cache (fetchPolicy: 'no-cache')
 * 8. CREATE-Guard: canCreate = !!label?.trim() && (selectedScopes?.length ?? 0) > 0 && !creating && !customDateInvalid
 *    null/undefined: label→undefined (falsy via ?.), selectedScopes→0 (via ?? 0) — guard zu false, kein throw
 * 9. LABEL-INPUT: maxLength 255 (matching DB VARCHAR(255)), (label ?? '').trim() vor Übertragung
 * 10. SCOPE-LABELS: displayName aus SCOPE_METADATA (via groupScopesWithMetadata) ist einzige
 *     erlaubte Quelle für scope-Anzeigenamen — kein raw-scope-split ('lesen' via ':')
 * 11. CUSTOM-DATE-GUARD (RITA-1385): customDateInvalid = expiryOption==='custom' && (!customDate || customDate < todayBerlin).
 *     todayBerlin via Intl.DateTimeFormat('en-US',{timeZone:'Europe/Berlin'}).formatToParts — NICHT toISOString(UTC).
 *     customDateInvalid ist AUSSCHLIESSLICH abgeleitet (kein useState) — Quellen: expiryOption, customDate, todayBerlin.
 *     AK-3: expires_at-UserInputError vom BE wird als verständliche deutsche Meldung angezeigt (kein raw-String).
 * 12. ANFÜHRUNGSZEICHEN-VERTRAG (RITA-1384, file-wide): Alle deutschen typografischen Anführungszeichen
 *     in dieser Datei verwenden ausschließlich U+201E „ als öffnendes und U+201C " als schließendes Zeichen.
 *     U+201D " (RIGHT DOUBLE QUOTATION MARK, englisches Schließzeichen) ist VERBOTEN — erzeugt falschen Glyph.
 *     Kein HTML-Encoding — ausschließlich literale Unicode-Zeichen direkt im Quelltext.
 *     Gilt für ALLE gequoteten Strings in der Datei, nicht nur LANGDOCK_HINT.
 * 13. WRITE-IMPLIES-LOOKUP (RITA-1393/1400): Toggling 'Belege:schreiben' on auto-selects
 *     'Buchungskonten:lesen' + 'Steuersätze:lesen' + 'Berater:lesen'. Implied Scopes koennen NICHT abgewaehlt
 *     werden, solange 'Belege:schreiben' aktiv ist (§13b) — UI zeigt exakt die Scopes,
 *     die das Backend immer erteilt. Toggling 'Belege:schreiben' off entfernt Lookup-Scopes
 *     NICHT (koennen eigenstaendig gewaehlt sein). Kein neues Layout — nur Auswahl-Logik.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Box,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useMutation, useQuery } from '@apollo/client';
import getMcpCapabilitiesQuery from '../../Apollo/queries/getMcpCapabilities';
import createMcpKeyMutation from '../../Apollo/mutations/createMcpKey';
import revokeMcpKeyMutation from '../../Apollo/mutations/revokeMcpKey';
import {
  groupScopesWithMetadata,
  getExpiresAt as calcExpiresAt,
  clearedPhase,
  toggleScopeSelection,
  ExpiryOption,
} from './profileMcpUtils';

// CONTRACT §11: Berlin-Datum für min-Attribut und customDateInvalid-Prüfung.
// Intl.DateTimeFormat formatToParts — NICHT toISOString() (UTC-Offset) und NICHT toLocaleDateString('en-CA') (small-ICU-Trap).
function getTodayBerlin(): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export type McpKeyDialogMode = { type: 'CREATE' } | { type: 'REVOKE_CONFIRM'; id: string; label: string };

type InternalPhase =
  | { name: 'CREATE' }
  | { name: 'SHOW_KEY'; key: string; label: string }
  | { name: 'REVOKE_CONFIRM'; id: string; label: string };

interface McpKeyDialogProps {
  open: boolean;
  mode: McpKeyDialogMode;
  onClose: () => void;
  onKeyCreated: () => void;
  // CONTRACT: must be awaited — caller owns post-close refetch (kein fire-and-forget).
  // CONTRACT: caller MUST catch all errors internally and NEVER reject —
  //   dialog is unmounted after handleClose() and cannot surface post-close errors.
  onKeyRevoked: () => Promise<void>;
}

// CONTRACT §12: Literal Unicode U+201E/U+201C throughout this file — no HTML entities of any kind
// (no hex-numeric entities, no named quote-entities). Applies file-wide.
const LANGDOCK_HINT = 'Für Langdock: Diesen Schlüssel im Langdock-Workspace unter „Integrationen → MCP“ eintragen.';

function McpKeyDialog({ open, mode, onClose, onKeyCreated, onKeyRevoked }: McpKeyDialogProps) {
  const [internalPhase, setInternalPhase] = useState<InternalPhase>(() =>
    mode.type === 'REVOKE_CONFIRM' ? { name: 'REVOKE_CONFIRM', id: mode.id, label: mode.label } : { name: 'CREATE' },
  );

  const [label, setLabel] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [expiryOption, setExpiryOption] = useState<ExpiryOption>('none');
  const [customDate, setCustomDate] = useState('');
  const [copied, setCopied] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const {
    data: capData,
    loading: capLoading,
    error: capError,
  } = useQuery(getMcpCapabilitiesQuery, { skip: internalPhase.name !== 'CREATE' });

  // fetchPolicy: 'no-cache' — CONTRACT §7: plaintext NEVER in Apollo InMemoryCache
  const [createMcpKey, { loading: creating }] = useMutation(createMcpKeyMutation, {
    fetchPolicy: 'no-cache',
  });

  const [revokeMcpKey, { loading: revoking }] = useMutation(revokeMcpKeyMutation);

  // CONTRACT §13/§13b: Schreib-Scopes implizieren ihre Pflicht-Lookup-Lese-Scopes.
  // Die komplette Toggle-Logik lebt als pure, verhaltensgetestete Funktion in
  // profileMcpUtils.toggleScopeSelection (SCOPE_IMPLICATIONS ebd.; mirrors Backend
  // mcp/scopes.ts, durchgesetzt in createMcpKey.ts). RITA-1393/1400/1454.
  const handleScopeToggle = (scope: string) => {
    setSelectedScopes((prev: string[]) => toggleScopeSelection(prev, scope));
  };

  // CONTRACT §4a: Close in SHOW_KEY = Plaintext-Clearance via setInternalPhase(clearedPhase())
  const handleClose = () => {
    setLabel('');
    setSelectedScopes([]);
    setExpiryOption('none');
    setCustomDate('');
    setCopied(false);
    setMutationError(null);
    setInternalPhase(clearedPhase()); // Plaintext cleared — never persisted beyond this point
    onClose();
  };

  const handleCreate = async () => {
    setMutationError(null);
    try {
      const result = await createMcpKey({
        variables: {
          input: {
            label: (label ?? '').trim(),
            scopes: selectedScopes,
            expires_at: calcExpiresAt(expiryOption, customDate, new Date()),
          },
        },
      });
      const created = result.data?.createMcpKey;
      if (created?.key) {
        onKeyCreated();
        // CONTRACT §2: transition to SHOW_KEY — key lives ONLY in component state
        setInternalPhase({ name: 'SHOW_KEY', key: created.key, label: (label ?? '').trim() });
      }
    } catch (err: unknown) {
      // CONTRACT §11 AK-3: expires_at-Ablehnung vom BE als verständliche Meldung zeigen (kein raw-String).
      const rawMsg = err instanceof Error ? err.message : '';
      if (rawMsg.includes('expires_at darf nicht in der Vergangenheit liegen')) {
        setMutationError('Das Ablaufdatum darf nicht in der Vergangenheit liegen.');
      } else {
        setMutationError(rawMsg || 'Fehler beim Erstellen des Schlüssels');
      }
    }
  };

  const handleRevoke = async () => {
    if (internalPhase.name !== 'REVOKE_CONFIRM') return;
    setMutationError(null);
    try {
      await revokeMcpKey({ variables: { id: internalPhase.id } });
      // CONTRACT §9: Dialog schliesst sich ZUERST, DANN await Refetch beim Parent — kein fire-and-forget
      handleClose();
      await onKeyRevoked();
    } catch (err: unknown) {
      setMutationError(err instanceof Error ? err.message : 'Fehler beim Widerrufen des Schlüssels');
    }
  };

  const handleCopy = async () => {
    if (internalPhase.name !== 'SHOW_KEY') return;
    await navigator.clipboard.writeText(internalPhase.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // CONTRACT §11: Berlin-Datum und Validierungszustand für benutzerdefiniertes Ablaufdatum.
  // §11a (blank-date non-error): customDate === '' → helperText = 'Bitte ein Datum wählen.', error = false.
  //   Leeres Datum ist kein Fehler-Zustand — nur neutraler Hinweis. 'Datum darf nicht...' darf hier NICHT erscheinen.
  // §11b (past-date error flag): !!customDate && customDate < todayBerlin → helperText = 'Datum darf nicht...',
  //   error = true (aria-invalid="true"). customDateInvalid=true blockiert canCreate.
  // §11c (raw backend message suppression): rawMsg.includes('expires_at darf nicht...') →
  //   zeigt 'Das Ablaufdatum darf nicht in der Vergangenheit liegen.' (kein raw-String sichtbar).
  // §11d (derived only, no local state): customDateInvalid ist AUSSCHLIESSLICH aus expiryOption, customDate und todayBerlin abgeleitet — kein useState.
  const todayBerlin = getTodayBerlin();
  const customDateInvalid = expiryOption === 'custom' && (!customDate || customDate < todayBerlin);
  let customDateHelperText: string | undefined;
  if (expiryOption === 'custom') {
    if (!customDate) {
      customDateHelperText = 'Bitte ein Datum wählen.';
    } else if (customDate < todayBerlin) {
      customDateHelperText = 'Datum darf nicht in der Vergangenheit liegen.';
    }
  }

  // CONTRACT §8: null/undefined safe — label?.trim() returns undefined (falsy) for null/undefined;
  // (selectedScopes?.length ?? 0) returns 0 for null/undefined — never throws, always disables button.
  // CONTRACT §11: customDateInvalid blockiert canCreate wenn expiryOption==='custom' + Datum fehlt/Vergangenheit.
  const canCreate = !!label?.trim() && (selectedScopes?.length ?? 0) > 0 && !creating && !customDateInvalid;

  const scopeGroups = capData?.getMcpCapabilities?.scopes
    ? groupScopesWithMetadata(capData.getMcpCapabilities.scopes)
    : null;

  const hasBelegeSchreiben = capData?.getMcpCapabilities?.scopes?.includes('Belege:schreiben') ?? false;

  return (
    <Dialog open={open} onClose={internalPhase.name === 'SHOW_KEY' ? undefined : handleClose} maxWidth="sm" fullWidth>
      {internalPhase.name === 'CREATE' && (
        <>
          <DialogTitle>Neuen MCP-Schlüssel erstellen</DialogTitle>
          <DialogContent dividers>
            {mutationError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {mutationError}
              </Alert>
            )}

            <TextField
              label="Bezeichnung"
              fullWidth
              value={label}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLabel(e.target.value)}
              placeholder="z.B. Langdock Integration"
              sx={{ mb: 3 }}
              inputProps={{ 'data-testid': 'mcp-key-label-input', maxLength: 255 }}
            />

            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
              <FormLabel component="legend" sx={{ mb: 1 }}>
                Berechtigungen (Scopes)
              </FormLabel>
              {capLoading && <CircularProgress size={20} />}
              {capError && (
                <Alert severity="error">Berechtigungen konnten nicht geladen werden. Bitte Seite neu laden.</Alert>
              )}
              {!capLoading && !capError && scopeGroups && (
                <>
                  <FormGroup>
                    {scopeGroups.map(({ group, items }) => (
                      <Box key={group} sx={{ mb: 1.5 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
                        >
                          {group}
                        </Typography>
                        {items.map(({ scope, displayName, description }) => (
                          <FormControlLabel
                            key={scope}
                            control={
                              <Checkbox
                                checked={selectedScopes.includes(scope)}
                                onChange={() => handleScopeToggle(scope)}
                                size="small"
                                inputProps={
                                  {
                                    'data-testid': `scope-checkbox-${scope}`,
                                  } as React.InputHTMLAttributes<HTMLInputElement>
                                }
                              />
                            }
                            label={description ? `${displayName} — ${description}` : displayName}
                          />
                        ))}
                      </Box>
                    ))}
                  </FormGroup>
                  {hasBelegeSchreiben && (
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                      <Typography variant="caption">
                        <strong>Hinweis:</strong> „Belege schreiben“ benötigt zusätzlich die Stammdaten-Scopes
                        (Buchungskonten lesen, Steuersätze lesen, Berater lesen), damit KI-Integrationen die nötigen IDs
                        nachschlagen können — Berater lesen wird für die Beraterzuordnung des Belegs benötigt.
                      </Typography>
                    </Alert>
                  )}
                </>
              )}
              {!capLoading && !capError && !scopeGroups && (
                <Typography variant="body2" color="text.secondary">
                  Keine Berechtigungen verfügbar.
                </Typography>
              )}
            </FormControl>

            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ mb: 1 }}>
                Ablaufdatum
              </FormLabel>
              <RadioGroup
                value={expiryOption}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExpiryOption(e.target.value as ExpiryOption)}
              >
                <FormControlLabel value="none" control={<Radio size="small" />} label="Kein Ablaufdatum" />
                <FormControlLabel value="30d" control={<Radio size="small" />} label="30 Tage" />
                <FormControlLabel value="90d" control={<Radio size="small" />} label="90 Tage" />
                <FormControlLabel value="custom" control={<Radio size="small" />} label="Benutzerdefiniert" />
              </RadioGroup>
              {expiryOption === 'custom' && (
                <TextField
                  type="date"
                  value={customDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomDate(e.target.value)}
                  fullWidth
                  sx={{ mt: 1 }}
                  InputLabelProps={{ shrink: true }}
                  label="Datum"
                  inputProps={{ min: todayBerlin, 'data-testid': 'mcp-custom-date-input' }}
                  error={!!customDate && customDate < todayBerlin}
                  helperText={customDateHelperText}
                />
              )}
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={creating}>
              Abbrechen
            </Button>
            <Button
              onClick={handleCreate}
              variant="contained"
              disabled={!canCreate}
              data-testid="create-mcp-key-button"
            >
              {creating ? <CircularProgress size={20} /> : 'Schlüssel erstellen'}
            </Button>
          </DialogActions>
        </>
      )}

      {internalPhase.name === 'SHOW_KEY' && (
        <>
          <DialogTitle>MCP-Schlüssel: {internalPhase.label}</DialogTitle>
          <DialogContent dividers>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Dieser Schlüssel wird nur einmal gezeigt. Bitte jetzt kopieren und sicher aufbewahren.
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {LANGDOCK_HINT}
            </Typography>
            <TextField
              fullWidth
              value={internalPhase.key}
              InputProps={{
                readOnly: true,
                inputProps: { 'data-testid': 'mcp-key-plaintext' },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleCopy}
                      edge="end"
                      aria-label="Schlüssel kopieren"
                      data-testid="copy-mcp-key-button"
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {copied && (
              <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                Schlüssel kopiert!
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} variant="contained" data-testid="close-show-key-button">
              Schließen
            </Button>
          </DialogActions>
        </>
      )}

      {internalPhase.name === 'REVOKE_CONFIRM' && (
        <>
          <DialogTitle>Schlüssel widerrufen</DialogTitle>
          <DialogContent dividers>
            {mutationError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {mutationError}
              </Alert>
            )}
            <Alert severity="error">
              Schlüssel <strong>{internalPhase.label}</strong> wird sofort ungültig. Diese Aktion ist nicht umkehrbar.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={revoking} data-testid="cancel-revoke-button">
              Abbrechen
            </Button>
            <Button
              onClick={handleRevoke}
              variant="contained"
              color="error"
              disabled={revoking}
              data-testid="confirm-revoke-button"
            >
              {revoking ? <CircularProgress size={20} /> : 'Widerrufen'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

export default McpKeyDialog;
